/**
 * AnalysisResult Management API Client
 * Task 17.7: AnalysisResult管理UIの構築
 * Requirements: 23.1, 23.2, 23.3, 23.4, 23.5, 23.6, 23.7, 23.8, 23.9, 23.10, 23.11
 */
import { createAuthenticatedClient } from '../../../shared/api/axiosClient';

const API_BASE_URL = import.meta.env.VITE_API_URL || '';

let apiClient: ReturnType<typeof createAuthenticatedClient> | null = null;

const getApiClient = () => {
  if (!apiClient) {
    apiClient = createAuthenticatedClient(API_BASE_URL, () => {
      window.location.href = '/login';
    });
  }
  return apiClient;
};

export interface AnalysisResult {
  id: string;
  scores: Record<string, number>;
  patternDescription: string | null;
  createdAt: string;
  userSession: {
    id: string;
    startedAt: string;
    completedAt: string | null;
  };
  user: {
    id: string;
    name: string;
    email: string;
    username: string;
  };
  pattern: {
    id: string;
    name: string;
    description: string;
  } | null;
}

export interface GetAnalysisResultsParams {
  userId?: string;
  sessionId?: string;
  patternId?: string;
  createdAtFrom?: string;
  createdAtTo?: string;
  limit?: number;
  offset?: number;
}

export interface GetAnalysisResultsResponse {
  results: AnalysisResult[];
  pagination: {
    total: number;
    limit: number;
    offset: number;
    hasMore: boolean;
  };
}

export interface UpdateAnalysisResultInput {
  scores?: Record<string, number>;
  patternId?: string;
  patternDescription?: string;
}

export interface BulkDeleteInput {
  userIds?: string[];
  createdAtFrom?: string;
  createdAtTo?: string;
}

/**
 * Get analysis results with filters and pagination
 */
export async function getAnalysisResults(
  params: GetAnalysisResultsParams
): Promise<GetAnalysisResultsResponse> {
  const client = getApiClient();
  const response = await client.get<GetAnalysisResultsResponse>(
    '/admin/analysis-results',
    { params }
  );
  return response.data;
}

/**
 * Get analysis result by ID
 */
export async function getAnalysisResultById(id: string): Promise<AnalysisResult> {
  const client = getApiClient();
  const response = await client.get<{ result: AnalysisResult }>(
    `/admin/analysis-results/${id}`
  );
  return response.data.result;
}

/**
 * Update analysis result
 */
export async function updateAnalysisResult(
  id: string,
  input: UpdateAnalysisResultInput
): Promise<AnalysisResult> {
  const client = getApiClient();
  const response = await client.put<{ result: AnalysisResult }>(
    `/admin/analysis-results/${id}`,
    input
  );
  return response.data.result;
}

/**
 * Delete analysis result
 */
export async function deleteAnalysisResult(id: string): Promise<void> {
  const client = getApiClient();
  await client.delete(`/admin/analysis-results/${id}`);
}

/**
 * Bulk delete analysis results
 */
export async function bulkDeleteAnalysisResults(input: BulkDeleteInput): Promise<number> {
  const client = getApiClient();
  const response = await client.post<{ deletedCount: number }>(
    '/admin/analysis-results/bulk-delete',
    input
  );
  return response.data.deletedCount;
}

/**
 * Export analysis results as JSON
 */
export async function exportAnalysisResultsJSON(
  params: GetAnalysisResultsParams
): Promise<string> {
  const client = getApiClient();
  const response = await client.get('/admin/analysis-results/export', {
    params: { ...params, format: 'json' },
  });
  return response.data;
}

/**
 * Export analysis results as CSV
 */
export async function exportAnalysisResultsCSV(params: GetAnalysisResultsParams): Promise<string> {
  const client = getApiClient();
  const response = await client.get('/admin/analysis-results/export', {
    params: { ...params, format: 'csv' },
  });
  return response.data;
}
